/**
 * Facility configuration page with dashboard-family grouping and editing.
 */

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, openModal } from "risa-oasis-ui_v2";
import {
  ConfigSectionType,
  CONFIG_SECTION_TYPES,
} from "../../../../../shared/enums/rbacRoles";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import NewFacilityModal from "../../../../components/modals/newFacilityModal/newFacilityModal";
import { HealthCareFacilityModel } from "../../../../data-model/healthFacility";
import { fetchHealthFacilityFromFirebase } from "../../../../redux/slice/healthFacilitySlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import HealthCareDetails from "./components/healthCareDetails/healthCareDetails";
import HealthFacilityCard from "./components/healthFacilityCard/healthFacilityCard";

const SECTION_LABELS: Record<ConfigSectionType, string> = {
  med_onc: "MedOnc Facilities",
  pharmacy: "Pharmacy Facilities",
  claims: "Claims Facilities",
  clinical_trials: "Clinical Trials Facilities",
};

/**
 * Renders dashboard-filtered or grouped healthcare facility configuration.
 *
 * @param variant - Dashboard family to filter, or common for grouped display
 * @returns Facility configuration page
 */
const HealthFacilityConfiguration = ({
  variant = "med_onc",
}: {
  variant?: "med_onc" | "pharmacy" | "common";
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: healthFacilityData, loading } = useSelector(
    (state: RootState) => state.healthFacility,
  );

  const isCommon = variant === "common";

  const [healthFacilityList, setHealthFacilityList] = useState<
    HealthCareFacilityModel[]
  >([]);
  const [showNewFacilityModal, setShowNewFacilityModal] =
    useState<boolean>(false);
  const [selectedHealthCareFacilityIndex, setSelectedHealthCareFacilityIndex] =
    useState<number>(0);
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    dispatch(fetchHealthFacilityFromFirebase());
  }, [dispatch]);

  useEffect(() => {
    if (healthFacilityData && !loading) {
      setHealthFacilityList(healthFacilityData);
    } else {
      setHealthFacilityList([]);
    }
  }, [healthFacilityData, loading]);

  useEffect(() => {
    if (showNewFacilityModal) {
      openModal("new-facility-modal");
    }
  }, [showNewFacilityModal]);

  // For the dashboard-specific tabs (med_onc / pharmacy) only show facilities
  // tagged with that dashboard_type. The common tab shows everything, grouped.
  const visibleFacilities = isCommon
    ? healthFacilityList
    : healthFacilityList.filter((f) => f.dashboard_type === variant);

  useEffect(() => {
    if (isCommon) return;
    const firstVisibleIndex = healthFacilityList.findIndex(
      (f) => f.dashboard_type === variant,
    );
    setSelectedHealthCareFacilityIndex(
      firstVisibleIndex === -1 ? 0 : firstVisibleIndex,
    );
  }, [healthFacilityList, variant, isCommon]);

  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const buildGroups = () => {
    const groups: {
      key: string;
      label: string;
      facilities: HealthCareFacilityModel[];
    }[] = [];
    for (const type of CONFIG_SECTION_TYPES) {
      groups.push({
        key: type,
        label: SECTION_LABELS[type],
        facilities: healthFacilityList.filter((f) => f.dashboard_type === type),
      });
    }
    const other = healthFacilityList.filter((f) => {
      const dt = f.dashboard_type;
      return !dt || !CONFIG_SECTION_TYPES.includes(dt);
    });
    if (other.length) {
      groups.push({
        key: "other",
        label: "Other Facilities",
        facilities: other,
      });
    }
    return groups;
  };

  const renderCard = (healthFacility: HealthCareFacilityModel) => (
    <div
      key={healthFacility.DocId ?? ""}
      onClick={() => {
        setSelectedHealthCareFacilityIndex(
          healthFacilityList.indexOf(healthFacility),
        );
      }}
    >
      <HealthFacilityCard
        healthCareData={healthFacility}
        isActive={
          selectedHealthCareFacilityIndex ===
          healthFacilityList.indexOf(healthFacility)
        }
        showChip={isCommon}
      />
    </div>
  );

  return (
    <div className="health-facility-config layout flex h-full flex-col gap-4 overflow-hidden">
      {loading ? (
        <>
          <div className="h-full w-full">
            <LoaderMessage message={"Loading health care facilities..."} />
          </div>
        </>
      ) : (
        <>
          <div className="health-facility-config header flex items-center justify-between border-b border-gray-200 pb-2">
            <div className="health-facility-config header text text-h11 font-semibold">
              Health Care Facility Configuration
            </div>
            <div className="health-facility-config header action">
              <Button
                disabled={false}
                children={"Add New Facility"}
                onClick={() => {
                  setShowNewFacilityModal(true);
                }}
                buttonType={"primary"}
                size={"medium"}
              />
            </div>
          </div>
          <div className="health-facility-config body flex flex-1 gap-4 overflow-hidden">
            <div className="health-facility-config body left b flex flex-1 flex-col gap-4 overflow-auto">
              {isCommon
                ? buildGroups().map((group) => (
                    <div key={group.key} className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => toggleSection(group.key)}
                        className="flex items-center justify-between border-b border-gray-200 pb-1 text-left text-small font-semibold text-primaryGray-5"
                      >
                        <span>
                          {group.label} ({group.facilities.length})
                        </span>
                        <span
                          className={`transition-transform ${
                            collapsedSections[group.key] ? "" : "rotate-90"
                          }`}
                        >
                          ▶
                        </span>
                      </button>
                      {!collapsedSections[group.key] &&
                        (group.facilities.length ? (
                          <div className="flex flex-col gap-4">
                            {group.facilities.map(renderCard)}
                          </div>
                        ) : (
                          <div className="text-tiny text-primaryGray-9">
                            No facilities
                          </div>
                        ))}
                    </div>
                  ))
                : visibleFacilities.map(renderCard)}
            </div>
            <div className="health-facility-config body right flex-1 overflow-hidden">
              <HealthCareDetails
                singleHealthCareData={
                  healthFacilityList[selectedHealthCareFacilityIndex]
                }
                showChip={isCommon}
                variant={variant}
              />
            </div>
          </div>
          {showNewFacilityModal && (
            <NewFacilityModal
              data={null}
              onModalClose={() => setShowNewFacilityModal(false)}
              type={"add"}
              variant={variant}
            />
          )}
        </>
      )}
    </div>
  );
};

export default HealthFacilityConfiguration;
